// HEADER-DOCS-012: EditDocumentModal
// Spec: §4.3, §15.1 (visibleToCounselor locked), §19.C
//
// Metadata + visibility edit with optimistic concurrency (expectedUpdatedAt -> 409 stale_document).
// Read-only for parents and for students looking at a document they did not create.

import React, { useState } from 'react';
import toast from 'react-hot-toast';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  Link,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DownloadIcon from '@mui/icons-material/Download';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

import {
  documentsApi,
  DocumentDto,
  DocType,
  DOC_TYPES,
  DOC_TYPE_LABELS,
  httpStatusOf,
  isApiError,
} from '../../../../services/camMirrorService';
import { triggerDownload } from '../../../../utils/triggerDownload';
import { DeleteConfirmModal } from '../../../../components/studentActionBar/DeleteConfirmModal';
import { canModifyDoc, ViewerRole } from './documentPermissions';
import { DocFieldLabel, docFieldSx } from './DocFieldLabel';

// Human-readable file size, e.g. "842 KB" / "2.4 MB".
function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

interface EditDocumentModalProps {
  doc: DocumentDto;
  viewerRole: ViewerRole;
  currentUserId: number | null;
  onClose: () => void;
  onUpdated: (doc: DocumentDto) => void;
  onDeleted: () => void;
}

export const EditDocumentModal: React.FC<EditDocumentModalProps> = ({
  doc,
  viewerRole,
  currentUserId,
  onClose,
  onUpdated,
  onDeleted,
}) => {
  const [displayName, setDisplayName] = useState(doc.displayName);
  const [docType, setDocType] = useState<DocType>(doc.docType);
  const [visibleToStudent, setVisibleToStudent] = useState(doc.visibility.student);
  const [visibleToParent, setVisibleToParent] = useState(doc.visibility.parent);
  const [expectedUpdatedAt, setExpectedUpdatedAt] = useState(doc.updatedAt);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reloadMsg, setReloadMsg] = useState<string | null>(null);
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  // Edit and delete share one rule in v1 (counselor any, student own, parent none).
  const canModify = canModifyDoc(doc, viewerRole, currentUserId);
  const readonly = !canModify;

  const onSave = async () => {
    if (saving || readonly || !displayName.trim()) return;
    setError(null);
    setSaving(true);
    try {
      const updated = await documentsApi.update(doc.externalId, {
        displayName: displayName.trim(),
        docType,
        visibility: {
          student: visibleToStudent,
          parent: visibleToParent,
          // counselor NOT sent: server-forced true per §15.1 / §19.C
        },
        expectedUpdatedAt,
      });
      toast.success('Document updated.');
      onUpdated(updated);
    } catch (err) {
      const code = isApiError(err) ? err.response.data.code : undefined;
      if (code === 'stale_document') {
        const fresh = isApiError(err) ? err.response.data.currentDocument : undefined;
        if (fresh) {
          // Load the latest server copy so the next save carries the new updatedAt.
          setDisplayName(fresh.displayName);
          setDocType(fresh.docType);
          setVisibleToStudent(fresh.visibility.student);
          setVisibleToParent(fresh.visibility.parent);
          setExpectedUpdatedAt(fresh.updatedAt);
          setReloadMsg(
            'This document was edited by someone else. The latest version is now shown. Re-apply your changes if needed.'
          );
        } else {
          setError('This document was changed by someone else. Close and reopen it to see the latest.');
        }
      } else if (code === 'financial_doc_blocked') {
        setError(
          'That display name looks like a financial document (FAFSA, tax return, etc.). Please rename it or share it through the school financial aid portal.'
        );
      } else if (code === 'invalid_visibility') {
        setError('Counselor visibility cannot be turned off in this version.');
      } else if (code === 'document_not_found') {
        setError('This document no longer exists. Close this window to refresh the list.');
      } else {
        setError("We couldn't save that change. Please try again.");
      }
    } finally {
      setSaving(false);
    }
  };

  const isLink = doc.origin === 'link';
  const [downloading, setDownloading] = useState(false);

  // Same flow as DocumentCard.onDownload: always fetch a fresh presigned URL
  // (the list-time URL has a 5-minute TTL), then download in place.
  const onDownload = async () => {
    if (downloading) return;
    setDownloading(true);
    try {
      const url = (await documentsApi.getDownloadUrl(doc.externalId)).downloadUrl;
      triggerDownload(url, doc.displayName);
    } catch (err) {
      toast.error(
        httpStatusOf(err) === 410
          ? 'This file is no longer available.'
          : "We couldn't download the file. Please try again."
      );
    } finally {
      setDownloading(false);
    }
  };

  // Close the confirm dialog only on success; on failure it stays open and shows its own error.
  const onDelete = async () => {
    await documentsApi.softDelete(doc.externalId);
    setConfirmingDelete(false);
    toast.success('Document deleted.');
    onDeleted();
  };

  return (
    <>
      <Dialog open onClose={() => !saving && onClose()} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {readonly ? 'Document details' : 'Edit document'}
          <IconButton onClick={onClose} size="small" aria-label="Close" disabled={saving}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={3}>
            {reloadMsg && (
              <Alert severity="info" onClose={() => setReloadMsg(null)}>
                {reloadMsg}
              </Alert>
            )}

            {/* Usersnap #2757: static labels above the inputs (no MUI floating label). */}
            <Box>
              <DocFieldLabel htmlFor="edit-display-name">Display name</DocFieldLabel>
              <TextField
                id="edit-display-name"
                fullWidth
                size="small"
                sx={docFieldSx}
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value.slice(0, 200))}
                disabled={readonly || saving}
              />
            </Box>
            <Box>
              <DocFieldLabel id="edit-doc-type-label">Document type</DocFieldLabel>
              <FormControl fullWidth size="small" sx={docFieldSx} disabled={readonly || saving}>
                <Select
                  labelId="edit-doc-type-label"
                  value={docType}
                  onChange={(e) => setDocType(e.target.value as DocType)}
                >
                  {DOC_TYPES.map((t) => (
                    <MenuItem key={t} value={t}>
                      {DOC_TYPE_LABELS[t]}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            {/* Usersnap #2757 follow-up: clickable file/link, also shown in read-only mode. */}
            {(isLink ? !!doc.linkUrl : !!doc.attachment) && (
              <Box>
                <DocFieldLabel>{isLink ? 'Link' : 'File'}</DocFieldLabel>
                <Link
                  component={isLink ? 'a' : 'button'}
                  {...(isLink
                    ? { href: doc.linkUrl ?? undefined, target: '_blank', rel: 'noopener noreferrer' }
                    : { type: 'button', onClick: onDownload, disabled: downloading })}
                  underline="hover"
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 1,
                    maxWidth: '100%',
                    fontSize: 15,
                    fontWeight: 500,
                    textAlign: 'left',
                    wordBreak: 'break-all',
                    cursor: downloading ? 'progress' : 'pointer',
                  }}
                >
                  {downloading ? (
                    <CircularProgress size={16} />
                  ) : isLink ? (
                    <OpenInNewIcon fontSize="small" />
                  ) : (
                    <DownloadIcon fontSize="small" />
                  )}
                  {isLink ? doc.linkUrl : doc.displayName}
                </Link>
                {!isLink && doc.attachment && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                    {formatSize(doc.attachment.sizeBytes)} · Click to download
                  </Typography>
                )}
              </Box>
            )}

            {!readonly && (
              <>
                <Typography variant="subtitle2" sx={{ pt: 1 }}>
                  Visibility
                </Typography>
                <FormControlLabel
                  control={
                    <Switch
                      checked={visibleToStudent}
                      onChange={(e) => setVisibleToStudent(e.target.checked)}
                      disabled={saving}
                    />
                  }
                  label="Visible to student"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={visibleToParent}
                      onChange={(e) => setVisibleToParent(e.target.checked)}
                      disabled={saving}
                    />
                  }
                  label="Visible to parent"
                />
                {/* Counselor row intentionally not rendered per §15.1 / §19.C */}
              </>
            )}

            {error && <Alert severity="error">{error}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between' }}>
          {canModify ? (
            <IconButton
              size="small"
              color="error"
              onClick={() => setConfirmingDelete(true)}
              disabled={saving}
              aria-label="Delete document"
            >
              <DeleteOutlineIcon />
            </IconButton>
          ) : (
            <Box />
          )}
          <Box>
            <Button onClick={onClose} disabled={saving}>
              {readonly ? 'Close' : 'Cancel'}
            </Button>
            {!readonly && (
              <Button
                variant="contained"
                onClick={onSave}
                disabled={saving || !displayName.trim()}
                sx={{ ml: 1 }}
              >
                {saving ? <CircularProgress size={16} /> : 'Save'}
              </Button>
            )}
          </Box>
        </DialogActions>
      </Dialog>

      {confirmingDelete && (
        <DeleteConfirmModal
          itemName={`"${doc.displayName}"`}
          onConfirm={onDelete}
          onCancel={() => setConfirmingDelete(false)}
        />
      )}
    </>
  );
};
